import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toolbarButtonSx } from "../../../components/common/toolbarStyles";
import { useNotify } from "../../../context/NotificationContext";
import { useListPersons } from "../hooks/useListPersons";
import PersonFields from "./PersonFields";
import { Person } from "../types";

/** Student → Guardians: fathers, mothers, guardians and emergency contacts (search, edit, delete, add). */
export default function PersonList() {
  const navigate = useNavigate();
  const notify = useNotify();
  const {
    persons,
    loading,
    error,
    page,
    rowsPerPage,
    totalElements,
    searching,
    handleChangePage,
    handleChangeRowsPerPage,
    actions,
  } = useListPersons();

  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; externalId: number } | null>(null);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, externalId: number) => setMenuAnchor({ el: event.currentTarget, externalId });
  const handleMenuClose = () => setMenuAnchor(null);

  // Search dialog
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchMobile, setSearchMobile] = useState("");

  const runSearch = async () => {
    await actions.search(searchName.trim(), searchMobile.trim());
    setSearchOpen(false);
  };

  const clearSearch = () => {
    actions.clearSearch();
    setSearchName("");
    setSearchMobile("");
  };

  // Edit dialog
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [editForm, setEditForm] = useState<Person | null>(null);
  const [saving, setSaving] = useState(false);

  const openEdit = (person: Person) => {
    setEditingPerson(person);
    setEditForm({ ...person });
    handleMenuClose();
  };

  const closeEdit = () => {
    setEditingPerson(null);
    setEditForm(null);
  };

  const handleSaveEdit = async () => {
    if (!editingPerson || !editForm) return;
    const changedFields: Partial<Person> = {};
    (Object.keys(editForm) as (keyof Person)[]).forEach((field) => {
      if (editForm[field] !== editingPerson[field]) {
        (changedFields as any)[field] = editForm[field];
      }
    });

    setSaving(true);
    try {
      await actions.update(editingPerson.externalId, changedFields);
      notify("Updated successfully.", "success");
      closeEdit();
    } catch (e) {
      notify("Failed to update.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Person | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await actions.remove(deleteTarget.externalId);
      notify("Deleted successfully.", "success");
    } catch (e) {
      notify("Failed to delete.", "error");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2, mr: 2 }}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Guardians
        </Typography>
        <Box flexGrow={1} />
        {searching && <Chip label="Showing search results" onDelete={clearSearch} size="small" />}
        <Button variant="outlined" size="small" sx={toolbarButtonSx} startIcon={<SearchIcon />} onClick={() => setSearchOpen(true)}>
          Search
        </Button>
        <Button variant="contained" size="small" sx={toolbarButtonSx} startIcon={<AddIcon />} onClick={() => navigate("/app/students/guardians/new")}>
          Add
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && persons.length > 0 && (
        <TableContainer className="mui-table-container-style">
          <Table size="small" className="mui-table-bordered">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>External ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Mobile No</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Date of Birth</TableCell>
                <TableCell>Qualification</TableCell>
                <TableCell>Occupation</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {persons.map((person: Person, index) => (
                <TableRow key={person.externalId ?? index}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{person.externalId}</TableCell>
                  <TableCell>{person.name}</TableCell>
                  <TableCell>{person.mobileNo || "—"}</TableCell>
                  <TableCell>{person.email || "—"}</TableCell>
                  <TableCell>{person.dob || "—"}</TableCell>
                  <TableCell>{person.qualification || "—"}</TableCell>
                  <TableCell>{person.occupation || "—"}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(event) => handleMenuOpen(event, person.externalId)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!searching && (
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              size="small"
            />
          )}
        </TableContainer>
      )}

      {!loading && persons.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {searching ? "No one matches your search." : "No guardians yet."}
        </Typography>
      )}

      {/* Row actions menu */}
      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            const person = persons.find((p) => p.externalId === menuAnchor?.externalId);
            if (person) openEdit(person);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            const person = persons.find((p) => p.externalId === menuAnchor?.externalId);
            if (person) setDeleteTarget(person);
            handleMenuClose();
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Search dialog */}
      <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>Search Guardians</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="Name" fullWidth size="small" value={searchName} onChange={(e) => setSearchName(e.target.value)} />
          <TextField label="Mobile No" fullWidth size="small" value={searchMobile} onChange={(e) => setSearchMobile(e.target.value)} />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSearchOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={runSearch} disabled={!searchName.trim() && !searchMobile.trim()}>
            Search
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={Boolean(editingPerson)} onClose={closeEdit} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>Edit</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          {editForm && <PersonFields value={editForm} onChange={setEditForm} />}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>Delete</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
